import {
  HarnessExecutionItem,
  HarnessExecutionPreview,
  HarnessExecutionRun,
  isExecutionInFlight,
} from '@flowx/designer/modules/ai-builder/types/harness.types'

/**
 * Where a run stands against the plan's steps: the one being written now, and the ones already
 * written. Keyed by work item id — the id an execution item's `item_id` carries.
 */
export type ExecutionProgress = {
  /** Only while the run is the harness's (queued or running): a paused or failed run has no step
   * in progress, only the one it stopped before. */
  activeId: string | null
  completed: ReadonlySet<string>
}

export const NO_PROGRESS: ExecutionProgress = { activeId: null, completed: new Set() }

/**
 * Reads the progress from the record's run. The harness names the running item in
 * `active_item_id`; a plan run's own item list is the fallback, and is also what says which items
 * are done. A single-item run is done when the run is.
 */
export function toExecutionProgress(run: HarnessExecutionRun | null): ExecutionProgress {
  if (!run) {
    return NO_PROGRESS
  }

  const items = run.type === 'plan' ? run.items : []
  const completed = new Set(
    run.type === 'plan'
      ? items.filter((item) => item.status === 'completed').map((item) => item.item_id)
      : run.status === 'completed'
        ? [run.item_id]
        : []
  )
  const running =
    run.active_item_id ??
    items.find((item) => item.status === 'running')?.item_id ??
    (run.type === 'plan' ? null : run.item_id)

  return {
    activeId:
      isExecutionInFlight(run.status) && running && !completed.has(running) ? running : null,
    completed,
  }
}

/** The steps a run has written, against every step there is to write. */
export type PlanProgress = { done: number; total: number }

/**
 * How far a run is through its plan: the run's own items, and every step the plan holds too — on a
 * story run those include the stories not opened yet, which the run's items do not list.
 */
export function toPlanProgress(
  run: HarnessExecutionRun | null,
  stepIds: readonly string[]
): PlanProgress {
  const { completed } = toExecutionProgress(run)
  const ids = new Set([
    ...(run?.type === 'plan' ? run.items.map((item) => item.item_id) : run ? [run.item_id] : []),
    ...stepIds,
  ])

  return { done: [...ids].filter((id) => completed.has(id)).length, total: ids.size }
}

/** A plan run with every step of its plan written. */
export function isEveryItemImplemented(
  run: HarnessExecutionRun | null,
  stepIds: readonly string[]
): boolean {
  const { done, total } = toPlanProgress(run, stepIds)

  return run?.type === 'plan' && done === total
}

/**
 * A plan run's or its review's items, by the work item id each one carries — what the spine folds
 * into its steps. A single item's run or review lists no items, so it reads as none.
 */
export function executionItemsById(
  runOrReview: HarnessExecutionPreview | HarnessExecutionRun | null
): ReadonlyMap<string, HarnessExecutionItem> {
  return new Map(
    runOrReview?.type === 'plan' ? runOrReview.items.map((item) => [item.item_id, item]) : []
  )
}

/** The item statuses a run left a step in without writing it. */
export const STOPPED_ITEM_STATUSES: ReadonlySet<string> = new Set(['failed', 'interrupted'])
